import { createRouter, createWebHistory } from 'vue-router';

import { isSpiritualHost, isStrictHost, SPIRITUAL_HOME_PATH, SITE } from '@/app-config';

import FinanceHome from '@/pages/FinanceHome.vue';
import FinancieleCoaching from '@/pages/FinancieleCoaching.vue';
import WieBenIk from '@/pages/WieBenIk.vue';
import Contact from '@/pages/Contact.vue';

import SpiritualHome from '@/pages/SpiritualHome.vue';
import SpiritueleCoaching from '@/pages/SpiritueleCoaching.vue';
import WieBenIk2 from '@/pages/WieBenIk2.vue';
import Contact2 from '@/pages/Contact2.vue';
import WatIsReiki from '@/pages/WatIsReiki.vue';
import ElementenHealing from '@/pages/ElementenHealing.vue';
import WatIsLeela from '@/pages/WatIsLeela.vue';

import NotFound from '@/pages/NotFound.vue';

// Decided once, at startup, from the domain the visitor opened.
const spiritualHost = isSpiritualHost();
const strictHost = isStrictHost(); // a recognised production domain (either site)

// Inner pages, per site. The landing page ("/") and the 404 catch-all are
// added per-scenario below so "/" always resolves to the right home.
const financePages = [
  { path: '/financiele-coaching', component: FinancieleCoaching, meta: { section: 'finance', title: 'Financiële coaching', description: 'Samen je administratie en geldzaken op orde, met heldere stappen en rapportages die je écht helpen beslissen.' } },
  { path: '/wie-ben-ik', component: WieBenIk, meta: { section: 'finance', title: 'Wie ben ik', description: 'Adriaan, oprichter van Serviceburo de Wilde. Over mijn kijk op geld, begeleiding en het positieve.' } },
  { path: '/contact', component: Contact, meta: { section: 'finance', title: 'Contact', description: 'Stuur een e-mail voor een vrijblijvend gesprek.' } },
];

const spiritualPages = [
  { path: '/spirituele-coaching', component: SpiritueleCoaching, meta: { section: 'spiritual', title: 'Spirituele coaching', description: 'Een traject van drie maanden waarin ik je reisgenoot ben, met onder meer Leela-sessies en Reiki.' } },
  { path: '/wie-ben-ik-2', component: WieBenIk2, meta: { section: 'spiritual', title: 'Wie ben ik', description: 'Adriaan. Over mijn achtergrond, mijn kijk op begeleiding en waarom ik dit doe.' } },
  { path: '/contact-2', component: Contact2, meta: { section: 'spiritual', title: 'Contact', description: 'Stuur een e-mail voor een vrijblijvende kennismaking.' } },
  { path: '/wat-is-reiki', component: WatIsReiki, meta: { section: 'spiritual', title: 'Wat is Reiki?', description: 'Een eerlijke uitleg over deze Japanse vorm van energiewerk, en wat je van een sessie kunt verwachten.' } },
  { path: '/elementen-healing', component: ElementenHealing, meta: { section: 'spiritual', title: 'Elementen Healing van Avelon', description: 'Healen met de vier elementen — aarde, water, vuur en lucht — in de traditie van Avelon: wat de techniek is en hoe die is doorgegeven.' } },
  { path: '/wat-is-leela', component: WatIsLeela, meta: { section: 'spiritual', title: 'Wat is Leela?', description: 'Het eeuwenoude Indiase spel van zelfkennis: waar Leela vandaan komt en wat een sessie je kan brengen.' } },
];

const financeHomeMeta = { section: 'finance', title: '', description: 'Financiële coaching in Delft. Persoonlijke begeleiding naar overzicht, rust en richting in je geld — zakelijk én privé.' };
const spiritualHomeMeta = { section: 'spiritual', title: '', description: 'Adriaans Helende Handen — spirituele coaching en energiewerk. Ruimte, verbinding en rust, in jouw tempo.' };

const notFound = (section) => ({
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  component: NotFound,
  meta: { section, title: 'Pagina niet gevonden' },
});

// Path prefixes that belong to the spiritual site (used on non-strict hosts,
// where both trees exist, to theme a 404 by which side the URL looks like).
const SPIRITUAL_PREFIXES = ['/spiritueel', '/spirituele-coaching', '/wie-ben-ik-2', '/contact-2', '/wat-is-reiki', '/elementen-healing', '/wat-is-leela'];

let routes;

if (spiritualHost) {
  // adriaanshelendehanden.nl — spiritual site only. Finance paths are not
  // registered, so they fall through to the (spiritual-styled) 404.
  routes = [
    { path: '/', component: SpiritualHome, meta: spiritualHomeMeta },
    { path: SPIRITUAL_HOME_PATH, redirect: '/' },
    ...spiritualPages,
    notFound('spiritual'),
  ];
} else if (strictHost) {
  // serviceburodewilde.nl — finance site only.
  routes = [
    { path: '/', component: FinanceHome, meta: financeHomeMeta },
    ...financePages,
    notFound('finance'),
  ];
} else {
  // localhost / 127.0.0.1 (prerender) / anything unknown: register BOTH trees
  // so dev and the build-time prerender can render every page. "/" = finance
  // home, "/spiritueel" = spiritual home. The 404 is themed by URL shape.
  routes = [
    { path: '/', component: FinanceHome, meta: financeHomeMeta },
    ...financePages,
    { path: SPIRITUAL_HOME_PATH, component: SpiritualHome, meta: spiritualHomeMeta },
    ...spiritualPages,
    notFound('finance'),
  ];
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  }
});

// Theme the 404 page for the site it was reached from: always the current
// host's site on a production domain; elsewhere, guessed from the URL.
router.beforeEach((to) => {
  if (to.name === 'NotFound') {
    const spiritual = spiritualHost
      || (!strictHost && SPIRITUAL_PREFIXES.some((p) => to.path === p || to.path.startsWith(p + '/')));
    to.meta.section = spiritual ? 'spiritual' : 'finance';
  }
});

// ---------------------------------------------------------------------------
// Per-page <head>: tab title, meta description, canonical URL and social
// (Open Graph / Twitter) tags. Canonical/OG URLs always use the real domain
// from app-config, so they stay correct during the build-time prerender too.
// ---------------------------------------------------------------------------
function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setNamedMeta(name, content) {
  upsert(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('name', name);
    return m;
  }).setAttribute('content', content);
}

function setPropMeta(property, content) {
  upsert(`meta[property="${property}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('property', property);
    return m;
  }).setAttribute('content', content);
}

function setCanonical(href) {
  upsert('link[rel="canonical"]', () => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    return l;
  }).setAttribute('href', href);
}

router.afterEach((to) => {
  const section = to.meta?.section || 'finance';
  const site = SITE[section] || SITE.finance;

  // On the spiritual site the canonical home is "/", not "/spiritueel".
  const path = (section === 'spiritual' && to.path === SPIRITUAL_HOME_PATH) ? '/' : to.fullPath;

  const title = to.meta?.title ? `${to.meta.title} — ${site.name}` : site.name;
  const description = to.meta?.description || site.description;
  const url = site.baseUrl + path;
  const image = site.baseUrl + site.image;

  document.title = title;
  setNamedMeta('description', description);
  setNamedMeta('apple-mobile-web-app-title', site.name);
  setCanonical(url);

  setPropMeta('og:type', 'website');
  setPropMeta('og:site_name', site.name);
  setPropMeta('og:locale', 'nl_NL');
  setPropMeta('og:title', title);
  setPropMeta('og:description', description);
  setPropMeta('og:url', url);
  setPropMeta('og:image', image);

  setNamedMeta('twitter:card', 'summary');
  setNamedMeta('twitter:title', title);
  setNamedMeta('twitter:description', description);
  setNamedMeta('twitter:image', image);
});

export default router;
